import type { AppSettingsResponse } from '../../types/generated/models/app-settings-response'
import type { AppSettingModel } from '../../types/generated/models/app-setting-model'
import type { SubscriptionPlanModel } from '../../types/generated/models/subscription-plan-model'
import type { CurrentSubscriptionModel } from '../../types/generated/models/current-subscription-model'
import type { ActiveFeatureModel } from '../../types/generated/models/active-feature-model'
import type { DeviceCategoryModel } from '../../types/generated/models/device-category-model'
import type { SupportedCurrencyModel } from '../../types/generated/models/supported-currency-model'
import type { FeatureDefinitionModel } from '../../types/generated/models/feature-definition-model'

/* ── State interface ── */

export interface AppSettingState {
  fileUploadSettings: {
    allowedCometExtensions: string[]
    allowedLantekExtensions: string[]
    maxFileSize: number
    maxAttachmentFileSize: number
    allowedAttachmentExtensions: string[]
  }
  accountLockoutSettings: {
    failedAttempts: number
    threshold: number
    duration: number
  }
  subscriptionPlans: SubscriptionPlanModel[]
  currentSubscription: CurrentSubscriptionModel
  activeFeatures: ActiveFeatureModel[]
  stripeCustomerPortalUrl: string
  deviceCategories: DeviceCategoryModel[]
  supportedCurrencies: SupportedCurrencyModel[]
  featureDefinitions: FeatureDefinitionModel[],
  teamViewerUrl: string,
  servicesUrl: string,
  shopUrl: string,
  helpUrl: string,
  adapterTipWearThresholds: { [key: string]: number },
  lastResponse: AppSettingsResponse | null,
  lastLoadedAt: number | null,
  loadedForCustomerId: number | string | null,
  error: boolean | null
  errorMessage: string | null,
  resetPinForDevices: string,
}

// const SET_APP_SETTING = 'SET_APP_SETTING'

enum AppSettingKey {
  FILE_UPLOAD_ALLOWED_COMET_EXTENSIONS = 'AppSettings:FileUpload:AllowedCometExtensions',
  FILE_UPLOAD_ALLOWED_LANTEK_EXTENSIONS = 'AppSettings:FileUpload:AllowedLantekExtensions',
  FILE_UPLOAD_MAX_SIZE = 'AppSettings:FileUpload:MaxSize',
  ACCOUNT_LOCKOUT_FAILED_ATTEMPTS = 'AppSettings:AccountLockout:FailedAttempts',
  ACCOUNT_LOCKOUT_THRESHOLD = 'AppSettings:AccountLockout:Threshold',
  ACCOUNT_LOCKOUT_DURATION = 'AppSettings:AccountLockout:Duration',
  FILE_UPLOAD_MAX_ATTACHMENT_SIZE = 'AppSettings:FileUpload:MaxAttachmentSize',
  FILE_UPLOAD_ALLOWED_ATTACHMENT_EXTENSIONS = 'AppSettings:FileUpload:AllowedAttachmentExtensions'
}

/**
 * Helper function to find setting value by key
 */
const getSettingValue = (settings: AppSettingModel[] | null | undefined, key: string): string | null => {
  return settings?.find(setting => setting.id === key)?.value ?? null
}

export const GET_APP_SETTING = 'GET_APP_SETTING'
export const GET_APP_SETTING_SUCCESS = 'GET_APP_SETTING_SUCCESS'

export const defaultAppSettingState: AppSettingState = {
  fileUploadSettings: {
    allowedCometExtensions: [],
    allowedLantekExtensions: [],
    maxFileSize: 0,
    maxAttachmentFileSize: 0,
    allowedAttachmentExtensions: []
  },
  accountLockoutSettings: {
    failedAttempts: 0,
    threshold: 0,
    duration: 0
  },
  subscriptionPlans: [],
  currentSubscription: {
    tier: 'basic',
    status: 'inactive',
    devices: 0,
    id: null,
    stripeSubscriptionId: null,
    deviceTier: '',
    checkoutPending: false,
    startDate: null,
    currentPeriodStart: null,
    currentPeriodEnd: null,
    monthlyPrice: 0,
    totalPrice: 0
  },
  activeFeatures: [],
  stripeCustomerPortalUrl: '',
  deviceCategories: [],
  supportedCurrencies: [],
  featureDefinitions: [],
  teamViewerUrl: '',
  servicesUrl: '',
  shopUrl: '',
  helpUrl: '',
  adapterTipWearThresholds: {
    Default: 4500
  },
  lastResponse: null,
  lastLoadedAt: null,
  loadedForCustomerId: null,
  error: null,
  errorMessage: null,
  resetPinForDevices: ''
}

export default (state: AppSettingState = defaultAppSettingState, action: any): AppSettingState => {
  switch (action.type) {
    case GET_APP_SETTING_SUCCESS: {
      const response: AppSettingsResponse = action.payload.data

      if (response.success && response.settings) {
        const settings = response.settings

        const allowedCometValue = getSettingValue(settings, AppSettingKey.FILE_UPLOAD_ALLOWED_COMET_EXTENSIONS)
        const allowedLantekValue = getSettingValue(settings, AppSettingKey.FILE_UPLOAD_ALLOWED_LANTEK_EXTENSIONS)
        const maxSizeValue = getSettingValue(settings, AppSettingKey.FILE_UPLOAD_MAX_SIZE)
        const failedAttemptsValue = getSettingValue(settings, AppSettingKey.ACCOUNT_LOCKOUT_FAILED_ATTEMPTS)
        const thresholdValue = getSettingValue(settings, AppSettingKey.ACCOUNT_LOCKOUT_THRESHOLD)
        const durationValue = getSettingValue(settings, AppSettingKey.ACCOUNT_LOCKOUT_DURATION)
        const maxAttachmentSizeValue = getSettingValue(settings, AppSettingKey.FILE_UPLOAD_MAX_ATTACHMENT_SIZE)
        const allowedAttachmentExtValue = getSettingValue(settings, AppSettingKey.FILE_UPLOAD_ALLOWED_ATTACHMENT_EXTENSIONS)

        return {
          ...state,
          fileUploadSettings: {
            allowedCometExtensions: allowedCometValue?.split(',') ?? [],
            allowedLantekExtensions: allowedLantekValue?.split(',') ?? [],
            maxFileSize: maxSizeValue ? parseInt(maxSizeValue, 10) : 0,
            maxAttachmentFileSize: maxAttachmentSizeValue ? parseInt(maxAttachmentSizeValue, 10) : 0,
            allowedAttachmentExtensions: allowedAttachmentExtValue?.split(',') ?? []
          },
          accountLockoutSettings: {
            failedAttempts: failedAttemptsValue ? parseInt(failedAttemptsValue, 10) : 0,
            threshold: thresholdValue ? parseInt(thresholdValue, 10) : 0,
            duration: durationValue ? parseInt(durationValue, 10) : 0
          },
          subscriptionPlans: (response.subscriptionPlans ?? state.subscriptionPlans) as any,
          currentSubscription: (response.currentSubscription ?? state.currentSubscription) as any,
          activeFeatures: (response.activeFeatures ?? []) as any,
          stripeCustomerPortalUrl: response.stripeCustomerPortalUrl ?? state.stripeCustomerPortalUrl,
          deviceCategories: (response.deviceCategories ?? state.deviceCategories) as any,
          supportedCurrencies: (response.supportedCurrencies ?? state.supportedCurrencies) as any,
          featureDefinitions: (response.featureDefinitions ?? state.featureDefinitions) as any,
          teamViewerUrl: response.teamViewerUrl ?? state.teamViewerUrl,
          servicesUrl: (response as any).servicesUrl ?? state.servicesUrl,
          shopUrl: (response as any).shopUrl ?? state.shopUrl,
          helpUrl: (response as any).helpUrl ?? state.helpUrl,
          adapterTipWearThresholds: response.adapterTipWearThresholds ?? state.adapterTipWearThresholds,
          lastResponse: response,
          lastLoadedAt: action.meta?.loadedAt ?? Date.now(),
          loadedForCustomerId: action.meta?.loadedForCustomerId ?? null,
          error: false,
          errorMessage: null,
          resetPinForDevices: response.resetPinForDevices ?? state.resetPinForDevices,
        }
      }

      return {
        ...state,
        error: true,
        errorMessage: action.payload.data.errors
      }
    }
    default:
      return state
  }
}
